import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * MessageInput - Chat input with typing indicator support
 * Apple iMessage-inspired design with light/dark mode support
 */

const MAX_MESSAGE_LENGTH = 2000;

export default function MessageInput({
  onSend,
  onTyping,
  disabled = false,
  placeholder = 'Type a message...',
  canShareFiles = false,
  onFileSelect
}) {
  const [text, setText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Handle typing indicator
  useEffect(() => {
    if (text && !isTyping) {
      setIsTyping(true);
      onTyping?.(true);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (isTyping) {
        setIsTyping(false);
        onTyping?.(false);
      }
    }, 2000);

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [text, onTyping, isTyping]);

  useEffect(() => {
    return () => {
      onTyping?.(false);
    };
  }, [onTyping]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const trimmedText = text.trim();

    if (!trimmedText || disabled) return;

    onSend(trimmedText);
    setText('');
    setIsTyping(false);
    onTyping?.(false);

    inputRef.current?.focus();
  }, [text, disabled, onSend, onTyping]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  }, [handleSubmit]);

  const handleFileClick = useCallback(() => {
    if (!canShareFiles || !onFileSelect) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx,.pdf';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        onFileSelect(file);
      }
    };
    input.click();
  }, [canShareFiles, onFileSelect]);

  const remainingChars = MAX_MESSAGE_LENGTH - text.length;
  const showCharCount = text.length > MAX_MESSAGE_LENGTH * 0.8;

  return (
    <form onSubmit={handleSubmit} className="bg-white/80 dark:bg-[#2c2c2e]/80 backdrop-blur-xl border-t border-[#d2d2d7] dark:border-white/10 px-4 py-3">
      <div className="flex gap-2 items-end">
        {/* File attachment button */}
        {canShareFiles && (
          <button
            type="button"
            onClick={handleFileClick}
            className="w-10 h-10 rounded-full bg-[#f5f5f7] dark:bg-white/10 hover:bg-[#e8e8ed] dark:hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06c] flex items-center justify-center transition-colors flex-shrink-0"
            title="Attach file"
          >
            <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </button>
        )}

        {/* Text input */}
        <div className="flex-1 bg-[#f5f5f7] dark:bg-white/10 rounded-2xl flex items-center px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#06c]/50 transition-all">
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            maxLength={MAX_MESSAGE_LENGTH}
            className="flex-1 bg-transparent text-[#1d1d1f] dark:text-white placeholder-[#86868b] outline-none text-[15px] resize-none max-h-32 min-h-[24px]"
            style={{ height: 'auto', minHeight: '24px' }}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px';
            }}
          />
        </div>

        {/* Send button */}
        <button
          type="submit"
          disabled={!text.trim() || disabled}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06c] ${
            text.trim() && !disabled
              ? 'bg-[#06c] hover:bg-[#0055d4] shadow-sm hover:scale-105 active:scale-95'
              : 'bg-[#f5f5f7] dark:bg-white/10 cursor-not-allowed'
          }`}
        >
          <svg className={`w-5 h-5 ${text.trim() && !disabled ? 'text-white' : 'text-[#86868b]'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      </div>

      {showCharCount && (
        <p className={`text-xs mt-2 text-right ${
          remainingChars < 100 ? 'text-orange-500' : 'text-[#86868b]'
        }`}>
          {remainingChars} characters remaining
        </p>
      )}
    </form>
  );
}
